import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  FaRegCheckCircle, FaBan, FaPercent, FaTrophy,
  FaRegCalendarAlt, FaChevronDown
} from 'react-icons/fa';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// ── Parseo de fechas (DD/MM/YYYY o YYYY-MM-DD) ───────────────────────────
const parseDate = (val) => {
  if (!val) return null;
  const s = String(val).trim();
  const dmY = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmY) return new Date(`${dmY[3]}-${dmY[2].padStart(2,'0')}-${dmY[1].padStart(2,'0')}T12:00:00`);
  const iso = s.slice(0, 10);
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d) ? null : d;
};

const calcDias = (item) => {
  const fin = parseDate(item.fin_cotizacion);
  const culm = parseDate(item.fecha_culminacion);
  if (!fin || !culm) return null;
  return Math.round((culm - fin) / (1000 * 60 * 60 * 24));
};

// ── Caché en memoria (persiste mientras la pestaña esté abierta) ──────────
const memCache = { data: null, ts: null };
const CACHE_KEY = 'estadisticas_ranking_menores_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hora en ms

const loadFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < CACHE_TTL) return data;
    localStorage.removeItem(CACHE_KEY);
  } catch { /* ignore */ }
  return null;
};

const saveToLocalStorage = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); }
  catch { /* ignore si excede cuota */ }
};

// ── Paleta y helpers visuales (mockup 14a) ───────────────────────────────
const VERDE = '#0f766e';
const VERDE_OSCURO = '#0f4f47';
const AZUL = '#14507d';
const ROJO = '#b42318';
const AMBAR = '#8a6412';

// Rampa de la mini-tendencia: del tono más claro al acento oscuro
const RAMPA = ['#cfe6dd', '#cfe6dd', '#cfe6dd', '#a7d0c6', '#a7d0c6', '#5fa398', '#5fa398', VERDE, VERDE_OSCURO];

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];

// Rejillas de las dos tablas (idénticas en cabecera y filas)
const GRID_DIA = '34px 152px minmax(0,1fr) 88px 108px 196px 104px 62px';
const GRID_RANK = '40px minmax(0,1fr) 78px 86px 80px 136px 86px 78px 94px';

const EstadisticasMenores = () => {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedTipo, setSelectedTipo] = useState('');
  const [sortConfig, setSortConfig]   = useState({ key: 'total', dir: 'desc' });
  const [isMobile, setIsMobile]       = useState(false);
  const [visibleRanking, setVisibleRanking] = useState(20);

  // Seguimiento diario de adjudicados
  const [adjDiarios, setAdjDiarios]   = useState([]);
  const [loadingAdj, setLoadingAdj]   = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [detailSortDir, setDetailSortDir] = useState('asc');
  const [dateFrom, setDateFrom]       = useState('2026-03-30');
  const [dateTo, setDateTo]           = useState('');

  const handleSort = (key) =>
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Fetch seguimiento diario de adjudicados (usa estadistica_menores)
  useEffect(() => {
    (async () => {
      try {
        setLoadingAdj(true);
        let all = [], from = 0;
        while (true) {
          const { data: rows, error: err } = await supabase
            .from('estadistica_menores')
            .select('fecha_culminacion, fin_cotizacion, cod_contratacion, entidad, descripcion, tipo, departamento, nomRazonSocial, preciototal')
            .not('fecha_culminacion', 'is', null)
            .range(from, from + 999);
          if (err) throw err;
          all = all.concat(rows || []);
          if (!rows || rows.length < 1000) break;
          from += 1000;
        }
        // Agrupar por fecha
        const byDay = {};
        all.forEach(r => {
          const d = r.fecha_culminacion.split('T')[0];
          if (!d || isNaN(new Date(d + 'T12:00:00').getTime())) return;
          if (!byDay[d]) byDay[d] = [];
          byDay[d].push(r);
        });
        const sorted = Object.entries(byDay)
          .map(([fecha, items]) => ({ fecha, items, total: items.length }))
          .sort((a, b) => b.fecha.localeCompare(a.fecha));
        setAdjDiarios(sorted);
        if (sorted.length > 0) setSelectedDay(sorted[0].fecha);
      } catch { /* silencioso */ }
      finally { setLoadingAdj(false); }
    })();
  }, []);

  useEffect(() => {
    // 1. Caché en memoria (misma sesión, instantáneo)
    if (memCache.data && Date.now() - memCache.ts < CACHE_TTL) {
      setData(memCache.data);
      setLoading(false);
      return;
    }
    // 2. Caché en localStorage (entre sesiones, < 1 hora)
    const cached = loadFromLocalStorage();
    if (cached) {
      memCache.data = cached;
      memCache.ts   = Date.now();
      setData(cached);
      setLoading(false);
      return;
    }
    // 3. Fetch completo desde Supabase
    (async () => {
      try {
        setLoading(true);
        let all = [], from = 0;
        while (true) {
          const { data: rows, error: err } = await supabase
            .from('estadistica_ranking_menores')
            .select('entidad, departamento, tipo, adj, des, total, tasa, prov_totales, prov_unicos, prom_conv_prov')
            .range(from, from + 999);
          if (err) throw err;
          all = all.concat(rows || []);
          if (!rows || rows.length < 1000) break;
          from += 1000;
        }
        memCache.data = all;
        memCache.ts   = Date.now();
        saveToLocalStorage(all);
        setData(all);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // Al cambiar de filtro se vuelve al primer tramo del ranking y del detalle diario
  useEffect(() => {
    setVisibleRanking(20);
  }, [selectedDept, selectedTipo, sortConfig, dateFrom, dateTo]);

  // ── Derivados ────────────────────────────────────────────────────────────
  const departamentos = Array.from(new Set(data.map(d => d.departamento).filter(Boolean))).sort();
  const tipos         = Array.from(new Set(data.map(d => d.tipo).filter(Boolean))).sort();

  const filtered = data
    .filter(d => !selectedDept || d.departamento === selectedDept)
    .filter(d => !selectedTipo || d.tipo === selectedTipo);

  const totalAdj    = filtered.reduce((s, d) => s + (d.adj || 0), 0);
  const totalDes    = filtered.reduce((s, d) => s + (d.des || 0), 0);
  const totalGlobal = totalAdj + totalDes;
  const tasaGlobal  = totalGlobal > 0 ? ((totalAdj / totalGlobal) * 100).toFixed(1) : '0.0';
  const pctAdj      = totalGlobal > 0 ? (totalAdj / totalGlobal) * 100 : 0;
  const pctDes      = totalGlobal > 0 ? (totalDes / totalGlobal) * 100 : 0;

  // Ranking de departamentos por tasa (mínimo 5 registros totales, datos globales)
  const dptoMap = {};
  data.forEach(d => {
    const k = d.departamento || 'Sin especificar';
    if (!dptoMap[k]) dptoMap[k] = { adj: 0, total: 0 };
    dptoMap[k].adj   += d.adj   || 0;
    dptoMap[k].total += d.total || 0;
  });
  const dptoRank = Object.entries(dptoMap)
    .filter(([, v]) => v.total >= 5)
    .map(([nombre, v]) => ({ nombre, tasa: (v.adj / v.total) * 100 }))
    .sort((a, b) => b.tasa - a.tasa);
  const topDpto3 = dptoRank.slice(0, 3);
  const topDpto  = topDpto3[0];

  // Filas para la tabla (datos ya vienen pre-calculados)
  const rows = filtered
    .map(r => ({
      entidad:     r.entidad     || 'Sin especificar',
      tipo:        r.tipo        || '—',
      adj:         r.adj         || 0,
      des:         r.des         || 0,
      total:       r.total       || 0,
      tasa:        (() => { const a = r.adj || 0; const d = r.des || 0; const t = a + d; return t > 0 ? parseFloat(((a / t) * 100).toFixed(1)) : 0; })(),
      provTotales: r.adj || 0,
      provUnicos:  r.prov_unicos  || 0,
      tasaProv:    Number(r.prov_unicos) > 0 ? Number(r.total || 0) / Number(r.prov_unicos) : 0,
    }))
    .sort((a, b) => (a[sortConfig.key] - b[sortConfig.key]) * (sortConfig.dir === 'desc' ? -1 : 1));

  // ── Seguimiento diario: rango, día activo y estadísticas de cabecera ─────
  // Los filtros de la barra superior aplican también al seguimiento diario
  const adjFiltrados = adjDiarios
    .map((d) => {
      if (!selectedDept && !selectedTipo) return d;
      const items = d.items.filter((it) =>
        (!selectedDept || it.departamento === selectedDept) &&
        (!selectedTipo || it.tipo === selectedTipo));
      return { ...d, items, total: items.length };
    })
    .filter((d) => d.total > 0);

  const adjEnRango = adjFiltrados.filter((d) => {
    if (dateFrom && d.fecha < dateFrom) return false;
    if (dateTo && d.fecha > dateTo) return false;
    return true;
  });
  const adjAsc = [...adjEnRango].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const effectiveDay = adjEnRango.find((d) => d.fecha === selectedDay)
    ? selectedDay
    : (adjEnRango[0]?.fecha || null);
  const dayData = adjEnRango.find((d) => d.fecha === effectiveDay);

  const totalAdjDiarios = adjEnRango.reduce((s, d) => s + d.total, 0);
  const diasConDatos    = adjEnRango.length;
  const promedioDiario  = diasConDatos ? Math.round(totalAdjDiarios / diasConDatos) : 0;
  const maxDia = adjEnRango.reduce((max, d) => (d.total > (max?.total || 0) ? d : max), null);
  const maxBarra = adjAsc.reduce((m, d) => Math.max(m, d.total), 0) || 1;
  const ultimosDias = adjAsc.slice(-6);

  // Serie mensual de adjudicados (mini-tendencia del KPI de tasa).
  // Respeta departamento y tipo, pero no el rango de fechas: es la tendencia larga.
  const mesesMap = {};
  adjFiltrados.forEach((d) => {
    const k = d.fecha.slice(0, 7);
    mesesMap[k] = (mesesMap[k] || 0) + d.total;
  });
  const serieMeses = Object.entries(mesesMap)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([mes, total]) => ({ mes, total }));
  const serieMini  = serieMeses.slice(-9);
  const maxSerie   = serieMini.reduce((m, s) => Math.max(m, s.total), 0) || 1;
  // El último mes suele estar en curso: la comparación usa los dos previos completos
  const mesesCompletos = serieMeses.slice(0, -1);
  const deltaMes = mesesCompletos.length >= 2
    ? (() => {
        const ult = mesesCompletos[mesesCompletos.length - 1].total;
        const prev = mesesCompletos[mesesCompletos.length - 2].total;
        return prev > 0 ? ((ult - prev) / prev) * 100 : null;
      })()
    : null;

  // ── Estilos y helpers ────────────────────────────────────────────────────
  const card = { background: '#fff', border: '1px solid #e2e6ee', borderRadius: 12 };
  const cardHead = {
    display: 'flex', alignItems: 'flex-start', gap: 16,
    padding: '15px 18px', borderBottom: '1px solid #eef1f5', flexWrap: 'wrap'
  };
  const selectStyle = {
    width: '100%', padding: '8px 30px 8px 11px', borderRadius: 8,
    border: '1px solid #e2e6ee', background: '#fafbfc', color: '#0f172a',
    fontSize: 12.5, fontWeight: 500, cursor: 'pointer', appearance: 'none',
    WebkitAppearance: 'none', fontFamily: 'inherit', outline: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238a94a6' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 11px center'
  };
  const filtroLabel = { fontSize: 11.5, fontWeight: 600, color: '#5b6779', marginBottom: 6 };
  const dateInput = {
    fontSize: 12.5, color: '#0f172a', border: 'none', background: 'transparent',
    fontFamily: 'inherit', outline: 'none', padding: 0, minWidth: 0, flex: 1
  };
  const statCell = { padding: isMobile ? '8px 12px' : '8px 15px' };
  const statLabel = { fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', color: '#8a94a6' };
  const statValue = { fontSize: 15, fontWeight: 600, color: '#0f172a', marginTop: 2, whiteSpace: 'nowrap' };
  const separador = <div style={{ width: 1, background: '#eef1f5' }} />;
  const thStyle = { fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', color: '#5b6779' };
  const elipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  // Chip de tipo de contrato: Bien en verde, Servicio en azul, resto neutro
  const tipoChip = (tipo) => {
    const t = String(tipo || '').toLowerCase();
    const pal = t.startsWith('bien')
      ? { color: VERDE_OSCURO, background: '#e8f2f0', borderColor: '#cfe6dd' }
      : t.startsWith('servicio')
        ? { color: AZUL, background: '#eff6fb', borderColor: '#cfe0ee' }
        : { color: '#5b6779', background: '#f4f6f8', borderColor: '#e2e6ee' };
    return {
      ...pal, fontSize: 11.5, fontWeight: 600, borderWidth: 1, borderStyle: 'solid',
      borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap'
    };
  };

  const tasaBar = (t) => (t >= 50 ? VERDE : t >= 30 ? AMBAR : ROJO);
  const diaChip = (d) => (d <= 7
    ? { color: VERDE_OSCURO, background: '#e8f2f0' }
    : d <= 30 ? { color: AMBAR, background: '#fdf7e7' } : { color: ROJO, background: '#fef3f2' });

  // `estadistica_ranking_menores` tiene una fila por entidad × departamento ×
  // tipo, así que contar filas no cuenta entidades: la misma entidad puede
  // aparecer decenas de veces (INIA, por ejemplo, en 50 filas).
  const contarEntidades = (lista) => new Set(lista.map((d) => d.entidad || 'Sin especificar')).size;
  const totalEntidades = contarEntidades(data);
  const entidadesFiltradas = contarEntidades(filtered);
  const totalConvocatorias = data.reduce((s, d) => s + (d.total || 0), 0);
  const fechaDatos = adjDiarios[0]?.fecha
    ? adjDiarios[0].fecha.split('-').reverse().join('-')
    : null;

  const money = (n) => Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const num = (n) => Number(n).toLocaleString('es-PE');
  const diaCorto = (f) => {
    const [, m, d] = f.split('-');
    return `${Number(d)} ${MESES_CORTOS[Number(m) - 1]}`;
  };

  const limpiarFiltros = () => {
    setSelectedDept('');
    setSelectedTipo('');
    setDateFrom('2026-03-30');
    setDateTo('');
  };
  const hayFiltros = !!(selectedDept || selectedTipo || dateTo || dateFrom !== '2026-03-30');

  const sortLabels = {
    total: 'Total', adj: 'Adjudicadas', des: 'Desiertos', tasa: 'Tasa adjudicación',
    provTotales: 'Convocatorias adjudicadas', provUnicos: 'Prov. únicos', tasaProv: 'Ratio'
  };

  // Segmentado del ranking: atajos a los tres ordenamientos más usados
  const segmentos = [
    { id: 'total', label: 'Total' },
    { id: 'tasa', label: 'Tasa adjudicación' },
    { id: 'provTotales', label: 'Proveedores' }
  ];

  const rankCols = [
    { label: '#', key: null, align: 'center' },
    { label: 'ENTIDAD', key: null, align: 'center' },
    { label: 'TOTAL', key: 'total', align: 'center' },
    { label: 'ADJUDICADAS', key: 'adj', align: 'center' },
    { label: 'DESIERTAS', key: 'des', align: 'center' },
    { label: 'TASA ADJUDICACIÓN', key: 'tasa', align: 'center' },
    { label: 'TOTALES', key: 'provTotales', align: 'center' },
    { label: 'ÚNICOS', key: 'provUnicos', align: 'center' },
    { label: 'RATIO', key: 'tasaProv', align: 'center' }
  ];

  const dayCols = [
    { label: '#', align: 'center' },
    { label: 'NOMENCLATURA', align: 'center' },
    { label: 'ENTIDAD Y DESCRIPCIÓN', align: 'center' },
    { label: 'TIPO', align: 'center' },
    { label: 'DPTO.', align: 'center' },
    { label: 'PROVEEDOR ADJUDICADO', align: 'center' },
    { label: 'PRECIO', align: 'center' }
  ];

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '14px 22px', background: '#fff', border: '1px solid #e2e6ee', borderRadius: 12 }}>
        <div style={{ width: 20, height: 20, border: '3px solid #e2e8f0', borderTop: `3px solid ${VERDE}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ color: '#5b6779', fontWeight: 600, fontSize: 14 }}>Cargando estadísticas…</span>
      </div>
      <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: ROJO, fontSize: 14 }}>
      Error al cargar los datos: {error}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Cabecera ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 20, paddingBottom: 16, borderBottom: '1px solid #e2e6ee', flexWrap: 'wrap'
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.09em', color: VERDE }}>ESTADÍSTICAS · CONTRATOS MENORES</div>
          <div style={{ fontSize: isMobile ? 21 : 25, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.02em', marginTop: 5, lineHeight: 1.15 }}>Análisis de Adjudicaciones en contratos menores</div>
          <div style={{ fontSize: 13, color: '#5b6779', marginTop: 4 }}>
            {num(totalEntidades)} entidades · {num(totalConvocatorias)} convocatorias
            {fechaDatos ? ` · datos al ${fechaDatos}` : ''}
          </div>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))', gap: 14, marginTop: 16 }}>

        {/* Adjudicadas */}
        <div style={{ animation: 'dvUp .32s ease both' }}>
          <div className="est-kpi" style={{ ...card, borderTop: `2px solid ${VERDE}`, borderRadius: 11, padding: '15px 17px', height: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: '#5b6779' }}>ADJUDICADAS</span>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: '#f4f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FaRegCheckCircle style={{ fontSize: 14, color: '#a3abba' }} />
              </span>
            </div>
            <div style={{ fontSize: 31, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, marginTop: 9, animation: 'dvFade .55s ease both' }}>{num(totalAdj)}</div>
            <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#f1f4f8', marginTop: 11 }}>
              <span style={{ width: `${pctAdj}%`, background: VERDE, borderRadius: '3px 0 0 3px', transformOrigin: 'left', animation: 'dvBar .55s ease both' }} />
            </div>
            <div style={{ fontSize: 11.5, color: '#5b6779', marginTop: 7 }}>{pctAdj.toFixed(1)}% del total de convocatorias</div>
          </div>
        </div>

        {/* Desiertas */}
        <div style={{ animation: 'dvUp .32s ease .04s both' }}>
          <div className="est-kpi" style={{ ...card, borderTop: `2px solid ${ROJO}`, borderRadius: 11, padding: '15px 17px', height: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: '#5b6779' }}>DESIERTAS</span>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: '#f4f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FaBan style={{ fontSize: 14, color: '#a3abba' }} />
              </span>
            </div>
            <div style={{ fontSize: 31, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, marginTop: 9, animation: 'dvFade .55s ease .04s both' }}>{num(totalDes)}</div>
            <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#f1f4f8', marginTop: 11 }}>
              <span style={{ width: `${pctDes}%`, background: ROJO, borderRadius: '3px 0 0 3px', transformOrigin: 'left', animation: 'dvBar .55s ease .04s both' }} />
            </div>
            <div style={{ fontSize: 11.5, color: '#5b6779', marginTop: 7 }}>{pctDes.toFixed(1)}% quedó sin adjudicar</div>
          </div>
        </div>

        {/* Tasa de adjudicación */}
        <div style={{ animation: 'dvUp .32s ease .08s both' }}>
          <div className="est-kpi" style={{ ...card, borderTop: `2px solid ${VERDE_OSCURO}`, borderRadius: 11, padding: '15px 17px', height: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: '#5b6779' }}>TASA DE ADJUDICACIÓN</span>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: '#f4f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FaPercent style={{ fontSize: 13, color: '#a3abba' }} />
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 31, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, animation: 'dvFade .55s ease .08s both' }}>{tasaGlobal}%</span>
              {deltaMes !== null && (
                <span style={{
                  fontSize: 11.5, fontWeight: 600, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap',
                  color: deltaMes >= 0 ? VERDE : ROJO,
                  background: deltaMes >= 0 ? '#e8f2f0' : '#fef3f2',
                  border: `1px solid ${deltaMes >= 0 ? '#cfe6dd' : '#f6d5d1'}`
                }}>
                  {deltaMes >= 0 ? '+' : ''}{deltaMes.toFixed(1)}% adjudicados vs. mes previo
                </span>
              )}
            </div>
            {serieMini.length > 1 ? (
              <>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 26, marginTop: 9 }}>
                  {serieMini.map((s, i) => (
                    <span key={s.mes}
                      title={`${MESES_CORTOS[Number(s.mes.slice(5)) - 1]} · ${num(s.total)} adjudicados`}
                      style={{
                        flex: 1,
                        height: `${Math.max(6, (s.total / maxSerie) * 100)}%`,
                        background: RAMPA[RAMPA.length - serieMini.length + i] || RAMPA[0],
                        borderRadius: 2
                      }} />
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: '#5b6779', marginTop: 7 }}>
                  Adjudicados por mes · últimos {serieMini.length}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11.5, color: '#5b6779', marginTop: 11 }}>promedio global</div>
            )}
          </div>
        </div>

        {/* Mejor departamento */}
        <div style={{ animation: 'dvUp .32s ease .12s both' }}>
          <div className="est-kpi" style={{ ...card, borderTop: `2px solid ${AZUL}`, borderRadius: 11, padding: '15px 17px', height: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: '#5b6779' }}>MEJOR DEPARTAMENTO</span>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: '#eff6fb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FaTrophy style={{ fontSize: 13, color: '#7ea3c4' }} />
              </span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.15, marginTop: 10, wordBreak: 'break-word' }}>
              {topDpto ? topDpto.nombre : '—'}
              {topDpto && <span style={{ fontSize: 14, color: '#5b6779', fontWeight: 600 }}> {topDpto.tasa.toFixed(1)}%</span>}
            </div>
            {topDpto3.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 }}>
                {topDpto3.map((d, i) => (
                  <div key={d.nombre} style={{ display: 'grid', gridTemplateColumns: '64px minmax(0,1fr) 40px', alignItems: 'center', gap: 8 }}>
                    <span title={d.nombre} style={{ fontSize: 11.5, color: '#5b6779', ...elipsis }}>{d.nombre}</span>
                    <span style={{ height: 6, background: '#f1f4f8', borderRadius: 3 }}>
                      <span style={{
                        display: 'block', height: '100%', borderRadius: 3, background: AZUL,
                        width: `${(d.tasa / topDpto3[0].tasa) * 100}%`,
                        opacity: [1, 0.72, 0.55][i],
                        transformOrigin: 'left',
                        animation: `dvBar .55s ease ${(i * 0.06).toFixed(2)}s both`
                      }} />
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>{d.tasa.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Filtros (aplican al ranking y al rango del seguimiento) ── */}
      <div style={{ ...card, padding: '13px 16px', marginTop: 16, display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : 200 }}>
          <div style={filtroLabel}>Departamento</div>
          <select value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)} style={selectStyle}>
            <option value="">Todos los departamentos</option>
            {departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        {tipos.length > 1 && (
          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 200 }}>
            <div style={filtroLabel}>Tipo de contrato</div>
            <select value={selectedTipo} onChange={(e) => setSelectedTipo(e.target.value)} style={selectStyle}>
              <option value="">Todos los tipos</option>
              {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : 240 }}>
          <div style={filtroLabel}>Rango de fechas</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e2e6ee', background: '#fafbfc', borderRadius: 8, padding: '8px 11px' }}>
            <FaRegCalendarAlt style={{ fontSize: 14, color: '#8a94a6', flexShrink: 0 }} />
            <input type="date" min="2026-03-30" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={dateInput} />
            <span style={{ color: '#8a94a6', fontSize: 12.5 }}>–</span>
            <input type="date" min="2026-03-30" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={dateInput} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', background: '#f4f6f8', border: '1px solid #e2e6ee', borderRadius: 8, padding: '9px 13px', whiteSpace: 'nowrap' }}>
            {num(entidadesFiltradas)} entidades
          </span>
          {hayFiltros && (
            <button onClick={limpiarFiltros} className="est-btn"
              style={{ fontSize: 12.5, fontWeight: 600, color: '#5b6779', border: '1px solid #e2e6ee', borderRadius: 8, padding: '9px 13px', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* ── Seguimiento de adjudicados por día ── */}
      <div style={{ ...card, overflow: 'hidden', marginTop: 16 }}>
        <div style={cardHead}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Seguimiento de adjudicados por día</div>
            <div style={{ fontSize: 12.5, color: '#5b6779', marginTop: 3 }}>
              Convocatorias culminadas y adjudicadas · {num(totalAdjDiarios)} registros
            </div>
          </div>
          {!loadingAdj && diasConDatos > 0 && (
            <div style={{ display: 'flex', alignItems: 'stretch', background: '#fafbfc', border: '1px solid #eef1f5', borderRadius: 10, overflow: 'hidden' }}>
              <div style={statCell}>
                <div style={statLabel}>PROMEDIO DIARIO</div>
                <div style={statValue}>{num(promedioDiario)}</div>
              </div>
              {separador}
              <div style={statCell}>
                <div style={statLabel}>MÁXIMO</div>
                <div style={statValue}>
                  {num(maxDia?.total || 0)}
                  {maxDia && <span style={{ fontSize: 11.5, fontWeight: 500, color: '#8a94a6' }}> · {diaCorto(maxDia.fecha)}</span>}
                </div>
              </div>
              {separador}
              <div style={statCell}>
                <div style={statLabel}>DÍAS CON DATOS</div>
                <div style={statValue}>{num(diasConDatos)}</div>
              </div>
            </div>
          )}
        </div>

        {loadingAdj ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Cargando seguimiento…</div>
        ) : adjAsc.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin datos de culminación en el rango elegido.</div>
        ) : (
          <>
            {/* Tira de barras + últimos días */}
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #eef1f5', background: '#fafbfc' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 52 }}>
                {adjAsc.map((d) => {
                  const activo = d.fecha === effectiveDay;
                  return (
                    <button key={d.fecha} onClick={() => setSelectedDay(d.fecha)}
                      className={activo ? 'est-bar est-bar--activa' : 'est-bar'}
                      title={`${diaCorto(d.fecha)} · ${num(d.total)} adjudicados`}
                      style={{
                        flex: 1, minWidth: 0, padding: 0, border: 'none', cursor: 'pointer',
                        height: `${Math.max(2, (d.total / maxBarra) * 100)}%`,
                        background: activo ? VERDE_OSCURO : '#cfe6dd',
                        borderRadius: '2px 2px 0 0'
                      }} />
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, color: '#8a94a6', whiteSpace: 'nowrap' }}>{diaCorto(adjAsc[0].fecha)}</span>
                <span style={{ flex: 1, height: 1, background: '#e2e6ee', minWidth: 20 }} />
                <span style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {ultimosDias.map((d) => {
                    const activo = d.fecha === effectiveDay;
                    return (
                      <button key={d.fecha} onClick={() => setSelectedDay(d.fecha)}
                        className={activo ? undefined : 'est-btn'}
                        style={{
                          fontSize: 12, fontWeight: activo ? 600 : 500, fontFamily: 'inherit', cursor: 'pointer',
                          color: activo ? '#fff' : '#0f172a',
                          background: activo ? VERDE_OSCURO : '#fff',
                          border: `1px solid ${activo ? VERDE_OSCURO : '#e2e6ee'}`,
                          borderRadius: 7, padding: '5px 10px', whiteSpace: 'nowrap'
                        }}>
                        {diaCorto(d.fecha)} · {d.total}
                      </button>
                    );
                  })}
                </span>
              </div>
            </div>

            {dayData && (() => {
              const precioTotal = dayData.items.reduce((s, it) => s + (Number(it.preciototal) || 0), 0);
              const demoras = dayData.items.map(calcDias).filter((d) => d !== null);
              const demoraMedia = demoras.length ? Math.round(demoras.reduce((s, d) => s + d, 0) / demoras.length) : null;
              const ordenados = [...dayData.items]
                .map((item) => ({ item, dias: calcDias(item) }))
                .sort((a, b) => {
                  const da = a.dias ?? (detailSortDir === 'asc' ? Infinity : -Infinity);
                  const db = b.dias ?? (detailSortDir === 'asc' ? Infinity : -Infinity);
                  return detailSortDir === 'asc' ? da - db : db - da;
                });

              return (
                <>
                  {/* Resumen del día */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', background: '#f7faf9', borderBottom: '1px solid #e6eeeb', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', flex: 1, minWidth: 200, textTransform: 'capitalize' }}>
                      {new Date(dayData.fecha + 'T12:00:00').toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: VERDE_OSCURO, background: '#e8f2f0', border: '1px solid #cfe6dd', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap' }}>{dayData.total} adjudicados</span>
                    {precioTotal > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: AZUL, background: '#eff6fb', border: '1px solid #cfe0ee', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap' }}>S/ {money(precioTotal)}</span>
                    )}
                    {demoraMedia !== null && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#5b6779', background: '#fff', border: '1px solid #e2e6ee', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap' }}>Demora media {demoraMedia} d</span>
                    )}
                  </div>

                  {/* Tabla del día: se recorre con su propia barra, la
                      cabecera se queda fija arriba mientras se baja */}
                  <div style={{ overflow: 'auto', maxHeight: isMobile ? 360 : 520, overscrollBehavior: 'contain' }}>
                    <div style={{ minWidth: 1080 }}>
                      <div className="est-day-thead" style={{ ...thStyle, display: 'grid', gridTemplateColumns: GRID_DIA, alignItems: 'center', gap: 12, padding: '14px 18px', background: VERDE, borderBottom: `1px solid ${VERDE_OSCURO}`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16), 0 2px 4px rgba(15,79,71,.08)', position: 'sticky', top: 0, zIndex: 1, fontWeight: 700, lineHeight: 1.5, color: '#fff' }}>
                        {dayCols.map((c) => <div key={c.label} style={{ textAlign: c.align }}>{c.label}</div>)}
                        <div onClick={() => setDetailSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                          style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none', color: 'inherit' }}>
                          DÍAS {detailSortDir === 'asc' ? '▲' : '▼'}
                        </div>
                      </div>

                      {ordenados.map(({ item, dias }, j) => (
                        <div key={j} className="est-row" style={{
                          display: 'grid', gridTemplateColumns: GRID_DIA, alignItems: 'center', gap: 12,
                          padding: '11px 18px', borderBottom: '1px solid #f1f4f8',
                          background: j % 2 === 1 ? '#fafbfc' : '#fff'
                        }}>
                          <div style={{ textAlign: 'center', fontSize: 12, color: '#8a94a6' }}>{j + 1}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: VERDE_OSCURO, fontFamily: 'ui-monospace, Menlo, monospace', ...elipsis }}>{item.cod_contratacion || '—'}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.35, ...elipsis }}>{item.descripcion || '—'}</div>
                            <div style={{ fontSize: 11.5, color: '#5b6779', marginTop: 2, ...elipsis }}>{item.entidad || '—'}</div>
                          </div>
                          <div><span style={tipoChip(item.tipo)}>{item.tipo || '—'}</span></div>
                          <div style={{ fontSize: 12, color: '#5b6779', ...elipsis }}>{item.departamento || '—'}</div>
                          <div style={{ fontSize: 12.5, color: '#0f172a', ...elipsis }}>{item.nomRazonSocial || '—'}</div>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {item.preciototal != null ? money(item.preciototal) : '—'}
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            {dias !== null
                              ? <span style={{ ...diaChip(dias), fontSize: 11.5, fontWeight: 600, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>{dias} d</span>
                              : <span style={{ color: '#94a3b8' }}>—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pie del día */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 18px', background: '#fafbfc', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, color: '#5b6779' }}>{dayData.total} registros del día · precios en soles (S/)</span>
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* ── Ranking de entidades ── */}
      <div style={{ ...card, overflow: 'hidden', marginTop: 16 }}>
        <div style={cardHead}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Ranking de entidades</div>
            <div style={{ fontSize: 12.5, color: '#5b6779', marginTop: 3 }}>
              {selectedDept || 'Todos los departamentos'}{selectedTipo ? ` · ${selectedTipo}` : ''} · ordenado por {sortLabels[sortConfig.key].toLowerCase()}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 3, background: '#f4f6f8', borderRadius: 9, padding: 3 }}>
            {segmentos.map((s) => {
              const activo = sortConfig.key === s.id;
              return (
                <button key={s.id}
                  className={`est-seg${activo ? ' est-seg--activo' : ''}`}
                  onClick={() => setSortConfig({ key: s.id, dir: 'desc' })}
                  style={{
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 12.5, fontWeight: activo ? 600 : 500,
                    color: activo ? '#fff' : '#5b6779',
                    background: activo ? VERDE : 'transparent',
                    borderRadius: 7, padding: '6px 13px', whiteSpace: 'nowrap',
                    boxShadow: activo ? '0 1px 2px rgba(15,23,42,.12)' : 'none'
                  }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 1020 }}>
            {/* Pestañas de grupo */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID_RANK, gap: 10, padding: '7px 18px 0', background: '#f4f6f8' }}>
              <div style={{ gridColumn: 'span 2' }} />
              <div style={{ gridColumn: 'span 4', fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: AZUL, background: '#eff6fb', border: '1px solid #cfe0ee', borderBottom: 'none', borderRadius: '6px 6px 0 0', padding: '4px 9px', textAlign: 'center' }}>CONVOCATORIAS</div>
              <div style={{ gridColumn: 'span 3', fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: VERDE_OSCURO, background: '#e8f2f0', border: '1px solid #cfe6dd', borderBottom: 'none', borderRadius: '6px 6px 0 0', padding: '4px 9px', textAlign: 'center' }}>PROVEEDORES</div>
            </div>

            {/* Cabecera de columnas */}
            <div style={{ ...thStyle, display: 'grid', gridTemplateColumns: GRID_RANK, alignItems: 'center', gap: 10, padding: '8px 18px', background: '#f4f6f8', borderBottom: '1px solid #e2e6ee' }}>
              {rankCols.map((c) => {
                const activo = c.key && sortConfig.key === c.key;
                return (
                  <div key={c.label}
                    onClick={c.key ? () => handleSort(c.key) : undefined}
                    style={{
                      textAlign: c.align,
                      color: activo ? '#0f172a' : '#5b6779',
                      cursor: c.key ? 'pointer' : 'default',
                      userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                    {c.label}{activo ? (sortConfig.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </div>
                );
              })}
            </div>

            {/* Filas */}
            {rows.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No hay datos con los filtros seleccionados</div>
            ) : rows.slice(0, visibleRanking).map((r, i) => {
              const accent = i < 3 ? [VERDE_OSCURO, '#4b7f77', '#94a9c9'][i] : null;
              return (
                <div key={`${r.entidad}-${i}`} className="est-row" style={{
                  display: 'grid', gridTemplateColumns: GRID_RANK, alignItems: 'center', gap: 10,
                  padding: '11px 18px', borderBottom: '1px solid #f1f4f8',
                  background: i % 2 === 1 ? '#fafbfc' : '#fff',
                  boxShadow: accent ? `inset 3px 0 0 ${accent}` : 'none'
                }}>
                  <div style={{ textAlign: 'center', fontSize: 13, fontWeight: i < 3 ? 600 : 400, color: i < 3 ? '#0f172a' : '#5b6779' }}>{i + 1}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span title={r.entidad} style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', ...elipsis }}>{r.entidad}</span>
                    <span style={{ ...tipoChip(r.tipo), flexShrink: 0 }}>{r.tipo}</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{num(r.total)}</div>
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: VERDE }}>{num(r.adj)}</div>
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: ROJO }}>{num(r.des)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, height: 6, background: '#f1f4f8', borderRadius: 3 }}>
                      <span style={{ display: 'block', width: `${r.tasa}%`, height: '100%', background: tasaBar(r.tasa), borderRadius: 3, transformOrigin: 'left', animation: 'dvBar .55s ease both' }} />
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', width: 44, textAlign: 'right' }}>{r.tasa.toFixed(1)}%</span>
                  </div>
                  <div title="Convocatorias adjudicadas de la entidad" style={{ textAlign: 'center', fontSize: 12.5, color: '#0f172a' }}>{num(r.provTotales)}</div>
                  <div title="Proveedores distintos que aparecen en las convocatorias de la entidad, adjudicadas y desiertas" style={{ textAlign: 'center', fontSize: 12.5, color: '#5b6779' }}>{num(r.provUnicos)}</div>
                  <div title="Convocatorias de la entidad (adjudicadas + desiertas) entre los proveedores distintos que aparecieron en ellas. 1,00 = cada proveedor aparece una sola vez; cuanto más alto, más concentrada está la contratación en pocos proveedores." style={{ textAlign: 'center', fontSize: 12.5, color: '#5b6779' }}>{r.provUnicos > 0 ? r.tasaProv.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                </div>
              );
            })}
          </div>
        </div>

        {rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '12px 18px', background: '#fafbfc', borderTop: '1px solid #eef1f5', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: '#5b6779', flex: 1, minWidth: 140 }}>{num(entidadesFiltradas)} entidades · {num(rows.length)} filas</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: VERDE, whiteSpace: 'nowrap' }}>{num(totalAdj)} adjudicadas</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: ROJO, whiteSpace: 'nowrap' }}>{num(totalDes)} desiertas</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>Tasa global {tasaGlobal}%</span>
            {visibleRanking < rows.length && (
              <button onClick={() => setVisibleRanking((v) => v + 20)} className="est-link"
                style={{ fontSize: 12.5, fontWeight: 600, color: VERDE, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                Ver más
                <FaChevronDown style={{ fontSize: 12 }} />
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
        @keyframes dvUp { from { opacity:0; transform:translateY(7px); } to { opacity:1; transform:none; } }
        @keyframes dvFade { from { opacity:0; } to { opacity:1; } }
        @keyframes dvBar { from { transform:scaleX(0); } to { transform:scaleX(1); } }

        .est-day-thead > div { position: relative; }
        .est-day-thead > div:nth-child(n + 3)::before { content: ''; position: absolute; left: -6px; top: 50%; height: 14px; width: 1px; background: rgba(255,255,255,.22); transform: translateY(-50%); }

        .est-kpi { transition: box-shadow .18s ease, transform .18s ease; }
        .est-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(15,23,42,.09); }

        .est-btn:hover { background:#f4f6f8 !important; }
        .est-link:hover { text-decoration: underline; }
        .est-row:hover { background:#fafdfc !important; }
        .est-bar { transition: background .15s ease; }
        .est-bar:hover { background:${VERDE} !important; }
        .est-bar--activa:hover { background:${VERDE_OSCURO} !important; }
        .est-seg:hover { background: rgba(255,255,255,.6) !important; }
        .est-seg--activo:hover { background:${VERDE_OSCURO} !important; }

        select:focus { border-color:${VERDE} !important; box-shadow:0 0 0 3px rgba(15,118,110,.12) !important; }
      `}</style>
    </div>
  );
};

export default EstadisticasMenores;
